import { useEditor, EditorContent } from "@tiptap/react";
import { useState, useRef, useCallback, useEffect, useMemo, forwardRef, useImperativeHandle, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { CustomOrderedList } from "../editor/custom-ordered-list";
import { InlineMath, DisplayMath, NumberedEquation } from "../editor/math-nodes";
import { DocTitle, DocAuthor, DocDate, DocAbstract, DocToc, TheoremBlock, GenericEnv, TwoColumns } from "../editor/latex-nodes";
import { FigureBlock, getAllProjectImages } from "../editor/figure-node";
import { TikzBlock } from "../editor/tikz-node";
import { SnippetExtension, createLatexSnippets, runHeadingEnterBehavior, runSnippetTabBehavior } from "../editor/snippet-extension";
import { LatexInputRules } from "../editor/latex-input-rules";
import { serializeToLatex } from "../editor/use-latex-serializer";
import { parseLatexToDoc } from "../editor/latex-parser";
import { LatexPanel } from "./LatexPanel";
import { DocHints, type HintItem } from "./DocHints";
import { useAutocomplete } from "../contexts/AutocompleteContext";
import { searchDocSnippets, type DocSnippetSuggestion } from "../editor/doc-semantic-search";
import { compileToPdf } from "../services/file-sync";
import { fixLatexError } from "../services/latex-autofix";
import { getStoredApiKey } from "../services/pdf-scanner";

// Cached snippets for direct handler lookup when applying hints
const _snippetDefs = createLatexSnippets();

export interface PaperEditorHandle {
    importLatex: (latex: string) => void;
    /**
     * Fire a single keystroke event directly into the live editor.
     * Used by the dev-only ReplayPanel to drive autonomous demo recording playback.
     */
    fireKeystroke: (event: import('../editor/keystroke-generator').KeystrokeEvent) => void;
}

interface PaperEditorProps {
    onLatexChange?: (latex: string) => void;
    onLatexFullChange?: (latex: string) => void;
    onDirty?: () => void;
    initialLatex?: string;
    compilePdf?: (latex: string, images?: Record<string, string>) => Promise<unknown>;
    documentName?: string;
}

const FONT_SIZES = [9, 10, 11, 12, 14, 16, 18];
const DEFAULT_FONT_INDEX = 2; // 11pt

export const PaperEditor = forwardRef<PaperEditorHandle, PaperEditorProps>(function PaperEditor({ onLatexChange, onLatexFullChange, onDirty, initialLatex, compilePdf, documentName }, ref) {
    const [latexOutput, setLatexOutput] = useState("");
    const [latexOutputFull, setLatexOutputFull] = useState("");
    const [fontSizeIndex, setFontSizeIndex] = useState(DEFAULT_FONT_INDEX);
    const [isCompiling, setIsCompiling] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const { hintsEnabled } = useAutocomplete();
    const hintsEnabledRef = useRef(hintsEnabled);
    hintsEnabledRef.current = hintsEnabled;

    // Ref to access the TipTap editor from closures that can't capture 'editor' directly (e.g. handlePaste)
    const editorRef = useRef<ReturnType<typeof useEditor>>(null);

    // Doc-mode autocomplete state
    const [docSuggestions, setDocSuggestions] = useState<DocSnippetSuggestion[]>([]);
    const [docHintIndex, setDocHintIndex] = useState(0);
    const [docHintVisible, setDocHintVisible] = useState(false);
    const [docHintPos, setDocHintPos] = useState<{ top: number; left: number } | null>(null);
    const currentWordRef = useRef("");

    // Draggable paper width
    const [paperWidth, setPaperWidth] = useState(() => {
        const saved = localStorage.getItem("seetex-paper-width");
        return saved ? Number(saved) : 860;
    });
    const paperDragRef = useRef<{ x: number; width: number; side: "left" | "right" } | null>(null);

    // Debounce timer for the serializer — avoids re-serializing on every keystroke
    const serializeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipPersistenceOnNextUpdateRef = useRef(false);

    const fontSize = FONT_SIZES[fontSizeIndex];

    // Persist paper width
    useEffect(() => {
        localStorage.setItem("seetex-paper-width", String(paperWidth));
    }, [paperWidth]);

    const onPaperResizeDown = useCallback((e: ReactPointerEvent<HTMLDivElement>, side: "left" | "right") => {
        e.preventDefault();
        paperDragRef.current = { x: e.clientX, width: paperWidth, side };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [paperWidth]);

    const onPaperResizeMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
        if (!paperDragRef.current) return;
        const { x, width, side } = paperDragRef.current;
        // Dragging the right handle right → wider; left handle left → wider
        const delta = side === "right" ? e.clientX - x : x - e.clientX;
        // Multiply by 2 because both sides expand symmetrically
        const newWidth = Math.max(560, Math.min(1400, width + delta * 2));
        setPaperWidth(newWidth);
    }, []);

    const onPaperResizeUp = useCallback(() => {
        paperDragRef.current = null;
    }, []);

    const decreaseFontSize = () => {
        setFontSizeIndex((i) => Math.max(0, i - 1));
    };
    const increaseFontSize = () => {
        setFontSizeIndex((i) => Math.min(FONT_SIZES.length - 1, i + 1));
    };

    const [pdfError, setPdfError] = useState<string | null>(null);
    const [isAutoFixing, setIsAutoFixing] = useState(false);

    const handleGetPdf = async () => {
        if (!latexOutputFull) return;
        setIsCompiling(true);
        setPdfError(null);
        const imgMap = getAllProjectImages();
        const images = imgMap.size > 0 ? Object.fromEntries(imgMap) : undefined;
        try {
            if (compilePdf) {
                await compilePdf(latexOutputFull, images);
            } else {
                const pdfPath = await compileToPdf(latexOutputFull);
                if (import.meta.env.DEV) console.log("PDF generated:", pdfPath);
            }
        } catch (err) {
            console.error("PDF compilation failed:", err);
            setPdfError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsCompiling(false);
        }
    };

    // Build a date string matching LaTeX \today style: "Month Day, Year"
    const todayString = useMemo(() => new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    }), []);

    const initialContent = useMemo(() => {
        if (initialLatex) {
            try {
                return parseLatexToDoc(initialLatex);
            } catch (err) {
                console.error(`[PaperEditor] initialContent parse failed:`, err);
                // fall back to blank template below
            }
        }
        return {
            type: "doc",
            content: [
                {
                    type: "docTitle",
                    content: [{ type: "text", text: "Title" }],
                },
                {
                    type: "docAuthor",
                    content: [{ type: "text", text: "Author(s)" }],
                },
                {
                    type: "docDate",
                    content: [{ type: "text", text: todayString }],
                },
                {
                    type: "docAbstract",
                    content: [
                        {
                            type: "paragraph",
                            content: [{ type: "text", text: "Write a short abstract here." }],
                        },
                    ],
                },
                {
                    type: "paragraph",
                    content: [],
                },
            ],
        };
    }, [initialLatex, todayString]);

    const syncSerializedLatex = useCallback((editorInstance: NonNullable<typeof editorRef.current>, notifyParent: boolean) => {
        const json = editorInstance.getJSON();
        const latexBody = serializeToLatex(json, false);
        const latexFull = serializeToLatex(json, true);
        setLatexOutput(latexBody);
        setLatexOutputFull(latexFull);
        if (notifyParent) {
            onLatexChange?.(latexBody);
            onLatexFullChange?.(latexFull);
        }
    }, [onLatexChange, onLatexFullChange]);

    // Detect if text looks like LaTeX
    const looksLikeLatex = (text: string): boolean => {
        // Check for common LaTeX patterns
        const latexPatterns = [
            /\\(documentclass|usepackage|begin|end|title|author|section|subsection)/,
            /\\[a-zA-Z]+\{/,  // Commands with braces
            /\$[^$]+\$/,       // Inline math
            /\\\[.*\\\]/s,    // Display math
        ];
        return latexPatterns.some(p => p.test(text));
    };

    // Stable callback — all captured values are refs or stable setState functions
    const onCurrentWordChange = useCallback((word: string) => {
        currentWordRef.current = word;
        if (hintsEnabledRef.current && word.length >= 3) {
            const results = searchDocSnippets(word);
            setDocSuggestions(results);
            setDocHintVisible(results.length > 0);
            setDocHintIndex(0);
        } else {
            setDocSuggestions([]);
            setDocHintVisible(false);
        }
    }, []);

    // Memoize extensions so TipTap doesn't recreate plugins on every render
    const extensions = useMemo(() => [
        StarterKit.configure({ orderedList: false }),
        CustomOrderedList,
        InlineMath,
        DisplayMath,
        NumberedEquation,
        DocTitle,
        DocAuthor,
        DocDate,
        DocAbstract,
        DocToc,
        TheoremBlock,
        GenericEnv,
        TwoColumns,
        FigureBlock,
        TikzBlock,
        LatexInputRules,
        SnippetExtension.configure({
            snippets: _snippetDefs,
            onCurrentWordChange,
        }),
        Placeholder.configure({
            placeholder: "Start typing… use Tab-triggered snippets: scn (section), oo (theorem), pf (proof), mm (inline math), dm (display math)",
        }),
    ], [onCurrentWordChange]);

    const editor = useEditor({
        extensions,
        content: initialContent,
        editorProps: {
            attributes: {
                class: "seetex-document",
                spellcheck: "false",
            },
            handlePaste: (view, event, _slice) => {
                const text = event.clipboardData?.getData("text/plain") || "";
                if (!text || !looksLikeLatex(text)) return false;

                // Hard cap: refuse files > 2 MB
                const byteSize = new Blob([text]).size;
                if (byteSize > 2 * 1024 * 1024) {
                    alert(`This file is ${(byteSize / 1024 / 1024).toFixed(1)} MB. Please open files larger than 2 MB using File → Open instead of pasting.`);
                    return true;
                }

                // Helper: replace the entire document with the parsed content.
                // Temporarily disables editor.isInitialized so TipTap's ReactRenderer
                // uses queueMicrotask instead of flushSync, avoiding React max-update-depth errors.
                const applyParsedDoc = (parsedDoc: any) => {
                    const ed = editorRef.current;
                    const docNode = view.state.schema.nodeFromJSON(parsedDoc);
                    const wasInitialized = ed?.isInitialized;
                    if (ed) ed.isInitialized = false;
                    try {
                        view.dispatch(
                            view.state.tr.replaceWith(0, view.state.doc.content.size, docNode.content)
                        );
                    } finally {
                        if (ed && wasInitialized !== undefined) ed.isInitialized = wasInitialized;
                    }
                };

                // For large pastes (> 80 KB) parse off the synchronous call stack
                // so the browser can repaint before the heavy work starts.
                if (byteSize > 80 * 1024) {
                    setIsParsing(true);
                    setTimeout(() => {
                        try {
                            applyParsedDoc(parseLatexToDoc(text));
                        } catch (err) {
                            if (import.meta.env.DEV) console.warn("LaTeX paste parsing failed", err);
                        } finally {
                            setIsParsing(false);
                        }
                    }, 0);
                    return true;
                }

                // Small paste — synchronous
                try {
                    applyParsedDoc(parseLatexToDoc(text));
                    return true;
                } catch (err) {
                    if (import.meta.env.DEV) console.warn("LaTeX paste parsing failed, falling back to plain text", err);
                }
                return false;
            },
        },
        onCreate({ editor }) {
            // Populate the LaTeX panel immediately without persisting.
            // Initial editor content may be a placeholder before cloud restore completes.
            syncSerializedLatex(editor, false);
        },
        onUpdate({ editor }) {
            const shouldPersist = !skipPersistenceOnNextUpdateRef.current;
            if (shouldPersist) {
                onDirty?.();
            }
            // Debounce: wait 400 ms of idle time before re-serializing.
            // This keeps the LaTeX panel in sync without stalling every keystroke.
            if (serializeTimer.current) clearTimeout(serializeTimer.current);
            serializeTimer.current = setTimeout(() => {
                syncSerializedLatex(editor, shouldPersist);
                if (!shouldPersist) {
                    skipPersistenceOnNextUpdateRef.current = false;
                }
            }, 400);
        },
    });

    // Keep editorRef in sync
    editorRef.current = editor;

    // Pending import: if importLatex is called before TipTap has initialised,
    // store the latex here and apply it once the editor becomes ready.
    const pendingImportRef = useRef<string | null>(null);

    const handleImportLatex = useCallback((latex: string) => {
        if (!latex) { console.error("[PaperEditor] importLatex called with falsy value"); return; }
        if (!editor) {
            pendingImportRef.current = latex;
            return;
        }
        pendingImportRef.current = null;
        skipPersistenceOnNextUpdateRef.current = true;
        const byteSize = new Blob([latex]).size;
        setIsParsing(true);
        setTimeout(() => {
            try {
                const doc = parseLatexToDoc(latex);
                const docNode = editor.state.schema.nodeFromJSON(doc);
                // Temporarily mark the editor as not-yet-initialised so TipTap's
                // ReactRenderer uses queueMicrotask instead of flushSync for each
                // new node view. Without this, hundreds of synchronous flushSync
                // calls exceed React's nested-update depth limit.
                const wasInitialized = editor.isInitialized;
                editor.isInitialized = false;
                try {
                    editor.view.dispatch(
                        editor.state.tr.replaceWith(0, editor.state.doc.content.size, docNode.content)
                    );
                } finally {
                    editor.isInitialized = wasInitialized;
                }
            } catch (err) {
                console.error("[PaperEditor] LaTeX import failed:", err);
                alert(`Failed to parse LaTeX (${(byteSize / 1024).toFixed(0)} KB). Check console for details.`);
            } finally {
                setIsParsing(false);
            }
        }, 0);
    }, [editor]);

    // Process any pending import once the editor becomes available
    useEffect(() => {
        if (editor && pendingImportRef.current) {
            const pending = pendingImportRef.current;
            pendingImportRef.current = null;
            handleImportLatex(pending);
        }
    }, [editor, handleImportLatex]);

    const handleAutoFix = useCallback(async () => {
        if (!latexOutputFull || !pdfError) return;
        if (!getStoredApiKey()) {
            alert("Please set your Anthropic API key first (used for PDF scanning).");
            return;
        }
        setIsAutoFixing(true);
        try {
            // Build a compile function for the retry loop
            const doCompile = async (latex: string) => {
                if (compilePdf) {
                    const imgMap = getAllProjectImages();
                    const images = imgMap.size > 0 ? Object.fromEntries(imgMap) : undefined;
                    await compilePdf(latex, images);
                } else {
                    await compileToPdf(latex);
                }
            };

            const fixed = await fixLatexError(latexOutputFull, pdfError, doCompile);
            handleImportLatex(fixed);
            setPdfError(null);
        } catch (err) {
            // fixLatexError guarantees the original is preserved on failure
            alert(`Auto-fix failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setIsAutoFixing(false);
        }
    }, [latexOutputFull, pdfError, handleImportLatex, compilePdf]);

    useImperativeHandle(ref, () => ({
        importLatex: handleImportLatex,
        fireKeystroke: (event) => {
            const ed = editorRef.current;
            if (!ed) return;

            // If a MathLive mathfield currently has focus, route events there
            // instead of forcing focus back to TipTap (which would collapse the math editor).
            const active = document.activeElement as (Element & { executeCommand?: (cmd: unknown) => void }) | null;
            const mathField = active?.tagName === 'MATH-FIELD' ? active : null;

            if (mathField?.executeCommand) {
                switch (event.kind) {
                    case 'insert':
                        if (event.text) {
                            if (event.text.length === 1) {
                                // Single printable char: dispatch a real keydown so
                                // MathEditor's capture-phase handler feeds it to the
                                // trigger engine (enabling auto-triggers like ff → \frac).
                                mathField.dispatchEvent(
                                    new KeyboardEvent('keydown', { key: event.text, code: `Key${event.text.toUpperCase()}`, bubbles: true, cancelable: true })
                                );
                            } else {
                                // Multi-char LaTeX command (e.g. \geq, \sqrt{ab}):
                                // insert directly — no trigger engine involvement needed.
                                mathField.executeCommand(['insert', event.text]);
                            }
                        }
                        break;
                    case 'delete':
                        // Dispatch real keydown so MathEditor's handler fires
                        mathField.dispatchEvent(
                            new KeyboardEvent('keydown', { key: 'Backspace', code: 'Backspace', bubbles: true, cancelable: true })
                        );
                        break;
                    case 'tab':
                        // Dispatch real Tab so MathEditor's Tab handler runs:
                        // it navigates placeholders, moves to end, then calls onEscape() to exit.
                        mathField.dispatchEvent(
                            new KeyboardEvent('keydown', { key: 'Tab', code: 'Tab', bubbles: true, cancelable: true })
                        );
                        break;
                    case 'escape':
                        mathField.dispatchEvent(
                            new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true })
                        );
                        break;
                }
                return;
            }

            // TipTap is active — focus it and dispatch the event normally.
            ed.view.focus();
            switch (event.kind) {
                case 'insert':
                    if (event.text) ed.view.dispatch(ed.state.tr.insertText(event.text));
                    break;
                case 'tab':
                    // Call the snippet handler directly — synthetic keydown events are
                    // not reliably processed by ProseMirror's keyboard shortcut system.
                    if (!runSnippetTabBehavior(ed, _snippetDefs)) {
                        ed.view.dom.dispatchEvent(
                            new KeyboardEvent('keydown', { key: 'Tab', code: 'Tab', bubbles: true, cancelable: true })
                        );
                    }
                    break;
                case 'newline':
                    // Same: use the direct handler for heading exit, fall back for all else.
                    if (!runHeadingEnterBehavior(ed)) {
                        ed.view.dom.dispatchEvent(
                            new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true })
                        );
                    }
                    break;
                case 'delete':
                    ed.view.dom.dispatchEvent(
                        new KeyboardEvent('keydown', { key: 'Backspace', code: 'Backspace', bubbles: true, cancelable: true })
                    );
                    break;
                case 'escape':
                    ed.view.dom.dispatchEvent(
                        new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true })
                    );
                    break;
            }
        },
    }), [handleImportLatex]);

    // Apply a doc hint selection: replace current word with trigger, then call handler directly
    const applyDocHint = useCallback((index: number) => {
        if (!editor || index < 0 || index >= docSuggestions.length) return;
        const suggestion = docSuggestions[index];
        const trigger = suggestion.trigger;
        const word = currentWordRef.current;
        if (!word) return;

        const { state } = editor.view;
        const { from } = state.selection;
        // Replace the current word with the trigger text
        editor
            .chain()
            .deleteRange({ from: from - word.length, to: from })
            .insertContent(trigger)
            .focus()
            .run();

        // Route through the same snippet logic used by real Tab presses.
        requestAnimationFrame(() => { runSnippetTabBehavior(editor, _snippetDefs); });

        setDocHintVisible(false);
        setDocSuggestions([]);
    }, [editor, docSuggestions]);

    // Update popup position using viewport coordinates (fixed positioning)
    useEffect(() => {
        if (!editor || !docHintVisible) return;
        try {
            const { from } = editor.state.selection;
            const coords = editor.view.coordsAtPos(from);
            setDocHintPos({
                top: coords.bottom + 4,
                left: coords.left,
            });
        } catch {
            // coordsAtPos can fail during unusual states
        }
    }, [editor, docHintVisible, docSuggestions]);

    // Keyboard handler for DocHints popup
    const handleDocHintKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!docHintVisible || docSuggestions.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setDocHintIndex((i) => (i + 1) % docSuggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setDocHintIndex((i) => (i - 1 + docSuggestions.length) % docSuggestions.length);
        } else if (e.key === "Enter" || e.key === "Tab") {
            // Apply selected hint — prevent default so Enter doesn't also insert a newline
            // and Tab doesn't also fire the snippet extension's word-match.
            e.preventDefault();
            e.stopPropagation();
            applyDocHint(docHintIndex);
        } else if (e.key === "Escape") {
            e.preventDefault();
            setDocHintVisible(false);
        }
    }, [docHintVisible, docSuggestions, docHintIndex, applyDocHint]);

    return (
        <>
            {pdfError && (
                <div className="confirm-modal-overlay" onClick={() => setPdfError(null)}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="confirm-modal-title">PDF compilation failed</h3>
                        <p className="confirm-modal-message">{pdfError}</p>
                        <div className="confirm-modal-actions">
                            <button
                                className="confirm-modal-cancel"
                                onClick={handleAutoFix}
                                disabled={isAutoFixing}
                                title="Use AI to automatically fix the compilation error"
                            >
                                {isAutoFixing ? "Fixing…" : "Auto-fix with AI"}
                            </button>
                            <button className="confirm-modal-confirm confirm-modal-confirm--primary" onClick={() => setPdfError(null)}>OK</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="paper-editor-container" onKeyDownCapture={handleDocHintKeyDown}>
                <div className="paper-toolbar" style={{ maxWidth: paperWidth }}>
                    {documentName && <span className="paper-toolbar-docname" title={documentName}>{documentName}</span>}
                    <div className="font-size-controls">
                        <button
                            className="font-btn"
                            onClick={decreaseFontSize}
                            disabled={fontSizeIndex === 0}
                            title="Decrease font size"
                        >
                            −
                        </button>
                        <span className="font-size-label">{fontSize}pt</span>
                        <button
                            className="font-btn"
                            onClick={increaseFontSize}
                            disabled={fontSizeIndex === FONT_SIZES.length - 1}
                            title="Increase font size"
                        >
                            +
                        </button>
                    </div>
                    <button
                        className="pdf-btn"
                        onClick={handleGetPdf}
                        disabled={isCompiling || !latexOutputFull}
                        title="Compile to PDF"
                    >
                        {isCompiling ? "Compiling…" : "Get PDF"}
                    </button>
                </div>
                <div className="paper-shell" style={{ maxWidth: paperWidth }}>
                    <div
                        className="paper-resize-handle paper-resize-handle--left"
                        onPointerDown={(e) => onPaperResizeDown(e, "left")}
                        onPointerMove={onPaperResizeMove}
                        onPointerUp={onPaperResizeUp}
                    />
                    <div
                        className="paper-resize-handle paper-resize-handle--right"
                        onPointerDown={(e) => onPaperResizeDown(e, "right")}
                        onPointerMove={onPaperResizeMove}
                        onPointerUp={onPaperResizeUp}
                    />
                    {isParsing && (
                        <div className="parse-overlay">
                            <span className="parse-spinner" /> Parsing document…
                        </div>
                    )}
                    <div className="paper-page" style={{ fontSize: `${fontSize}pt` }}>
                        <EditorContent editor={editor} />
                    </div>
                </div>
                {createPortal(
                    <DocHints
                        suggestions={docSuggestions.map(s => ({ trigger: s.trigger, name: s.name, matchedOn: s.matchedOn }))}
                        selectedIndex={docHintIndex}
                        onSelect={applyDocHint}
                        onHoverIndex={setDocHintIndex}
                        visible={docHintVisible}
                        position={docHintPos}
                    />,
                    document.body
                )}
                <LatexPanel latex={latexOutput} latexFull={latexOutputFull} onImport={handleImportLatex} />
            </div>
        </>
    );
});

